import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { useAppState } from '@/state/store';
import { useT } from '@/i18n';
import { ladderLabel } from '@/content/fate';
import { loadDiceHistory, saveDiceHistory } from '@/persistence/storage';
import {
  dndFaces,
  dndNotation,
  envEffect,
  envTag,
  fateFaces,
  fateRoll,
  formatModifier,
  intOf,
  nextRollId,
  pushRoll,
  rollDndTray,
  TRAY_DICE,
  TRAY_MAX_COUNT,
  type AdvantageChoice,
  type DndTrayRoll,
  type EnvId,
  type FateRoll,
  type TrayMode,
  type TrayRoll,
} from '@/domain/dice-tray';
import './dice-tray.css';

/** Environmental chips: id → icon+label key and the effect key it prints. */
const ENVS: EnvId[] = ['full-moon', 'comet', 'eclipse'];
const ENV_LABEL_KEY: Record<EnvId, string> = {
  'full-moon': 'dice.envFullMoon',
  comet: 'dice.envComet',
  eclipse: 'dice.envEclipse',
};

/** `+3`, `0`, `-2` — the ladder rung as a signed number. */
const signed = (value: number): string => (value > 0 ? `+${value}` : `${value}`);

/**
 * TR rungs come from the single source of truth (FATE_LADDER — @/content/fate,
 * docs/phase3-charcreation-rules.md §B.4). The EN rungs are NOT in any source
 * document (the Turkish playtest provides no English equivalents — A-20);
 * these are the standard Fate English adjectives, kept as clearly
 * supplementary UI text only.
 */
const FATE_LADDER_EN: Record<number, string> = {
  8: 'Legendary (+8)',
  7: 'Epic (+7)',
  6: 'Fantastic (+6)',
  5: 'Superb (+5)',
  4: 'Great (+4)',
  3: 'Good (+3)',
  2: 'Fair (+2)',
  1: 'Average (+1)',
  0: 'Mediocre (0)',
  [-1]: 'Poor (-1)',
  [-2]: 'Terrible (-2)',
  [-3]: 'Awful (-3)',
  [-4]: 'Abysmal (-4)',
};

/** The ladder adjective for a rung, in the active locale. */
const ladderText = (value: number, locale: 'tr' | 'en'): string =>
  locale === 'tr'
    ? `${ladderLabel(value)} (${signed(value)})`
    : (FATE_LADDER_EN[value] ?? signed(value));

export default function DiceRoller({ initiallyOpen = false }: { initiallyOpen?: boolean } = {}): ReactElement {
  const { campaign, ui } = useAppState();
  const t = useT();
  const locale = ui.locale;

  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [mode, setMode] = useState<TrayMode>(
    campaign?.ruleSystem === 'fate' ? 'fate' : 'dnd',
  );

  // Fate roller — kept as text so a half-typed "-2" never becomes 0 or NaN.
  const [fateSkill, setFateSkill] = useState('2');
  const [fateTarget, setFateTarget] = useState('2');
  const [fateResult, setFateResult] = useState<FateRoll | null>(null);

  // 5e roller
  const [sides, setSides] = useState(20);
  const [count, setCount] = useState(1);
  const [modifier, setModifier] = useState('4');
  const [advantage, setAdvantage] = useState<AdvantageChoice>('none');
  const [dndResult, setDndResult] = useState<DndTrayRoll | null>(null);

  const [env, setEnv] = useState<EnvId | null>(null);
  // The log outlives the tab: seeded from localStorage, written back on every
  // change. loadDiceHistory() swallows a missing/damaged key — and any
  // storage-less environment — so the popover always opens with a list.
  const [history, setHistory] = useState<TrayRoll[]>(() => loadDiceHistory());

  const panelRef = useRef<HTMLDivElement>(null);

  const skill = intOf(fateSkill, 0, -20, 20);
  const target = intOf(fateTarget, 0, -6, 14);
  const mod = intOf(modifier, 0, -20, 20);
  const advantageApplies = sides === 20 && count === 1;
  const envNow = envEffect(env, mode, sides);
  const previewNotation = dndNotation({ sides, count, modifier: mod, advantage });

  /* --------------------------------------------------------------- close */

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event: PointerEvent): void => {
      const panel = panelRef.current;
      if (panel && !panel.contains(event.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  /* ----------------------------------------------------------- persist */

  // Every roll (and the "clear" button, which sets []) lands in storage under
  // the active namespace. saveDiceHistory never throws, so a full or absent
  // localStorage costs the log, never the roll.
  useEffect(() => {
    saveDiceHistory(history);
  }, [history]);

  /* ---------------------------------------------------------------- roll */

  const record = (roll: Omit<TrayRoll, 'id'>): void => {
    setHistory((prev) => pushRoll(prev, { ...roll, id: nextRollId(prev) }));
  };

  const rollFateNow = (): void => {
    const roll = fateRoll({ skill, target, env });
    const ladder = ladderText(roll.ladderValue, locale);
    setFateResult(roll);
    record({
      mode: 'fate',
      notation: roll.notation,
      line: `${roll.notation} → ${fateFaces(roll.dice)} = ${signed(roll.rawTotal)} · ${ladder}`,
      total: roll.rawTotal,
      tone: 'normal',
      env,
      at: Date.now(),
    });
  };

  const rollDndNow = (): void => {
    const roll = rollDndTray({ sides, count, modifier: mod, advantage, env });
    setDndResult(roll);
    record({
      mode: 'dnd',
      notation: roll.notation,
      line: `${roll.notation} → ${dndFaces(roll)} = ${signed(roll.total)}`,
      total: roll.total,
      tone: roll.critical === 'hit' ? 'crit' : roll.critical === 'miss' ? 'fumble' : 'normal',
      env,
      at: Date.now(),
    });
  };

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (mode === 'fate') rollFateNow();
    else rollDndNow();
  };

  /* ------------------------------------------------------------ env copy */

  /** What the selected chip will do — printed BEFORE the roll, never after. */
  const envEffectText = (): string | null => {
    if (env === null) return null;
    if (env === 'full-moon') return t('dice.envFullMoonEffect');
    if (env === 'eclipse') return t('dice.envEclipseEffect');
    if (mode === 'fate') return t('dice.envCometEffectFate');
    return sides === 20 ? t('dice.envCometEffectTest') : t('dice.envCometEffectDnd');
  };
  const envText = envEffectText();

  /** History timestamp, 24h, per locale — the tray keeps no seconds. */
  const timeOf = (at: number): string =>
    new Date(at).toLocaleTimeString(locale === 'tr' ? 'tr-TR' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });

  /* -------------------------------------------------------------- render */

  return (
    <div className="dice-tray">
      <button
        type="button"
        className="btn secondary dice-toggle"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span aria-hidden="true">🎲</span>
        <span>{t('dice.title')}</span>
      </button>

      {isOpen && (
        <div
          className="dice-panel"
          role="dialog"
          aria-label={t('dice.title')}
          ref={panelRef}
        >
          <div className="tray-head">
            <div className="tray-seg" role="tablist" aria-label={t('dice.title')}>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'fate'}
                className={mode === 'fate' ? 'active' : ''}
                onClick={() => setMode('fate')}
              >
                {t('dice.modeFate')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'dnd'}
                className={mode === 'dnd' ? 'active' : ''}
                onClick={() => setMode('dnd')}
              >
                {t('dice.modeDnd')}
              </button>
            </div>
            <button
              type="button"
              className="tray-x"
              aria-label={t('dice.close')}
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>

          {/* Environmental conditions — one chip, and the tray says what it does */}
          <div className="tray-block">
            <span className="tray-label">{t('dice.envTitle')}</span>
            <div className="tray-chips">
              {ENVS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`tray-chip${env === id ? ' on' : ''}`}
                  aria-pressed={env === id}
                  onClick={() => setEnv(env === id ? null : id)}
                >
                  {t(ENV_LABEL_KEY[id])}
                </button>
              ))}
            </div>
            {envText && (
              <p className="tray-note">
                {t('dice.envHint', { text: envText })}
              </p>
            )}
          </div>

          <form onSubmit={onSubmit}>
            {/* ------------------------------------------------ FATE 4dF */}
            {mode === 'fate' && (
              <div className="tray-block">
                <div className="tray-row">
                  <label className="tray-label">
                    {t('dice.skill')}
                    <input
                      type="text"
                      inputMode="numeric"
                      value={fateSkill}
                      onChange={(e) => setFateSkill(e.target.value)}
                      onBlur={() => setFateSkill(String(skill))}
                    />
                  </label>
                  <label className="tray-label">
                    {t('dice.target')}
                    <input
                      type="text"
                      inputMode="numeric"
                      value={fateTarget}
                      onChange={(e) => setFateTarget(e.target.value)}
                      onBlur={() => setFateTarget(String(target))}
                    />
                  </label>
                </div>

                <button type="submit" className="btn tray-roll">
                  🎲 {t('dice.roll')}{' '}
                  <span className="tray-notation">{previewFate(skill + envNow.bonus)}</span>
                </button>

                {fateResult && (
                  <div className="tray-result" aria-live="polite">
                    <div className="tray-dice">
                      {fateResult.dice.map((die, i) => (
                        <span
                          key={i}
                          className={`tray-die ${die > 0 ? 'plus' : die < 0 ? 'minus' : 'zero'}`}
                          style={{ animationDelay: `${i * 55}ms` }}
                        >
                          {die > 0 ? '+' : die < 0 ? '−' : '0'}
                        </span>
                      ))}
                    </div>
                    <div className="tray-total">
                      {ladderText(fateResult.ladderValue, locale)}
                    </div>
                    <div className="tray-breakdown">
                      {fateResult.notation} → {fateFaces(fateResult.dice)} ={' '}
                      {signed(fateResult.rawTotal)}
                      {fateResult.envBonus !== 0 && (
                        <span className="tray-envmark">{envTag(env, envNow)}</span>
                      )}
                    </div>
                    <span className={`tray-outcome ${fateResult.outcome}`}>
                      {t(`dice.outcome.${fateResult.outcome}`)} ·{' '}
                      {t('dice.margin', { value: signed(fateResult.margin) })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* -------------------------------------------------- 5e d20 */}
            {mode === 'dnd' && (
              <div className="tray-block">
                <span className="tray-label">{t('dice.die')}</span>
                <div className="tray-chips">
                  {TRAY_DICE.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`tray-chip tray-diechip${sides === value ? ' on' : ''}`}
                      aria-pressed={sides === value}
                      onClick={() => setSides(value)}
                    >
                      d{value}
                    </button>
                  ))}
                </div>

                <div className="tray-row">
                  <span className="tray-label">
                    {t('dice.count')}
                    <span className="tray-stepper">
                      <button
                        type="button"
                        aria-label="−"
                        onClick={() => setCount((n) => Math.max(1, n - 1))}
                      >
                        −
                      </button>
                      <span className="tray-stepper-value">{count}</span>
                      <button
                        type="button"
                        aria-label="+"
                        onClick={() => setCount((n) => Math.min(TRAY_MAX_COUNT, n + 1))}
                      >
                        +
                      </button>
                    </span>
                  </span>
                  <label className="tray-label">
                    {t('dice.modifier')}
                    <span className="tray-stepper">
                      <button
                        type="button"
                        aria-label="−"
                        onClick={() => setModifier(String(Math.max(-20, mod - 1)))}
                      >
                        −
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={modifier}
                        onChange={(e) => setModifier(e.target.value)}
                        onBlur={() => setModifier(String(mod))}
                      />
                      <button
                        type="button"
                        aria-label="+"
                        onClick={() => setModifier(String(Math.min(20, mod + 1)))}
                      >
                        +
                      </button>
                    </span>
                  </label>
                </div>

                <span className="tray-label">{t('dice.advantage')}</span>
                <div className="tray-seg tray-adv">
                  {(['none', 'adv', 'dis'] as AdvantageChoice[]).map((choice) => (
                    <button
                      key={choice}
                      type="button"
                      disabled={!advantageApplies}
                      title={advantageApplies ? undefined : t('dice.advHint')}
                      className={advantage === choice ? 'active' : ''}
                      onClick={() => setAdvantage(choice)}
                    >
                      {t(`dice.adv.${choice}`)}
                    </button>
                  ))}
                </div>

                <button type="submit" className="btn tray-roll">
                  🎲 {t('dice.roll')} <span className="tray-notation">{previewNotation}</span>
                </button>

                {dndResult && (
                  <div className="tray-result" aria-live="polite">
                    <div className="tray-dice">
                      {dndResult.result.dice.map((die, i) => (
                        <span
                          key={i}
                          className={`tray-die${die.kept ? '' : ' dropped'}${
                            die.kept && dndResult.critical === 'hit' && die.value === 20
                              ? ' crit'
                              : die.kept && dndResult.critical === 'miss' && die.value === 1
                                ? ' fumble'
                                : ''
                          }`}
                          style={{ animationDelay: `${i * 55}ms` }}
                        >
                          {die.value}
                        </span>
                      ))}
                    </div>
                    <div
                      className={`tray-total${
                        dndResult.critical === 'hit'
                          ? ' crit'
                          : dndResult.critical === 'miss'
                            ? ' fumble'
                            : ''
                      }`}
                    >
                      {dndResult.total}
                    </div>
                    <div className="tray-breakdown">
                      {dndResult.notation} → {dndFaces(dndResult)} = {signed(dndResult.total)}
                      {envTag(env, dndResult.env) && (
                        <span className="tray-envmark">{envTag(env, dndResult.env)}</span>
                      )}
                    </div>
                    {dndResult.critical && (
                      <span className={`tray-crit ${dndResult.critical}`}>
                        {dndResult.critical === 'hit' ? t('dice.crit') : t('dice.fumble')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </form>

          {/* ------------------------------------------------- roll history */}
          <div className="tray-block tray-history">
            <div className="tray-history-head">
              <span className="tray-label">{t('dice.history')}</span>
              {history.length > 0 && (
                <button type="button" className="tray-link" onClick={() => setHistory([])}>
                  {t('dice.clear')}
                </button>
              )}
            </div>
            {history.length === 0 ? (
              <p className="tray-note">{t('dice.historyEmpty')}</p>
            ) : (
              <ul>
                {history.map((roll) => (
                  <li key={roll.id} className={`tray-hist-row ${roll.tone}`}>
                    <span className="tray-hist-line">{roll.line}</span>
                    <span className="tray-hist-meta">
                      <span className="tray-hist-time">{timeOf(roll.at)}</span>
                      <span className="tray-hist-total">{roll.total}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Fate notation preview under the roll button: `4dF+2`. */
function previewFate(skill: number): string {
  return `4dF${formatModifier(skill)}`;
}
